/*
 *  Interface représentant une affaire (project) telle qu'utilisée dans l'application React et l'application API
 */

import { StatusConfig } from '@common/components/StatusCard/StatusCard';
import {
	faBook,
	faCalendarDays,
	faCircleCheck,
	faCirclePlay,
	faClock,
	faFileAlt,
	faSpinner,
	faTimesCircle,
} from '@fortawesome/free-solid-svg-icons';
import { ActivityData } from 'features/activities';
import { AdvanceData } from 'features/advances/types/types';
import { CustomerInvoiceData } from 'features/customerInvoices';
import { CustomerOrderData } from 'features/customerOrders';
import { LogisticElementData } from 'features/logisticElements';
import { QuoteData } from 'features/quotes';
import { UserData } from 'features/users';

export interface ProjectData {
	// Obligatoires
	id: number;
	referenceNumber: string;
	// Champs simples
	status?: string;
	comments?: string;
	// Relations
	activities?: ActivityData[];
	advances?: AdvanceData[];
	customerInvoices?: CustomerInvoiceData[];
	customerOrders?: CustomerOrderData[];
	logisticElements?: LogisticElementData[];
	quote?: QuoteData | null;
	// Commandes fournisseur liées (calculé côté backend depuis les OrderLines de ce projet)
	linkedSupplierOrders?: {
		id: number;
		referenceNumber: string;
		tiersLabel?: string | null;
		latestVersionId?: number | null;
		latestVersionStatus?: string | null;
		/** Montant HT de la dernière version non annulée (sérialisé en string par Doctrine) */
		latestVersionTotalExclTax?: string | null;
	}[];
	// Dépenses liées à cette affaire (pour le tableau "Dépenses à refacturer")
	// Type inline pour éviter une dépendance circulaire avec features/expenses
	expenses?: {
		id: number;
		label: string;
		amountHtInvoiced?: number | null;
		supplierInvoice?: {
			id: number;
			referenceNumber?: string | null;
			penaltyAndCarePercent?: number | null;
		} | null;
		/** Présent si un élément logistique de refacturation a déjà été créé */
		logisticElement?: {
			id: number;
			unitPriceExclTax?: string | null;
			quantity?: string | null;
			/** Présent si l'élément logistique a été facturé au client */
			customerInvoice?: { id: number; referenceNumber?: string | null } | null;
		} | null;
	}[];
	// Métadonnées
	createdAt?: Date;
	createdBy?: UserData | null;
	// Champs agrégés (renseignés uniquement sur les endpoints de reporting)
	billableHours?: number;
	nonBillableHours?: number;
	totalHours?: number;
	totalExclTax?: number;
	latestActiveQuoteVersion?: {
		id: number;
		versionIndex: number;
		status: string;
		commentIndex: string;
	} | null;
}

/** Comparaison devis prévu / factures émises et payées, chargée à part de la fiche. */
export interface ProjectBudget {
	quoted: number;
	servicesQuoted: number;
	productsQuoted: number;
	billed: number;
	servicesBilled: number;
	productsBilled: number;
	paid: number;
	servicesPaid: number;
	productsPaid: number;
}

export const PROJECT_STATUS_CONFIG: StatusConfig = {
	steps: [
		{
			key: 'CANCELED',
			icon: faTimesCircle,
			color: 'bg-red-600 text-white',
			textColor: 'text-red-600',
			label: 'Annulée',
			side: true,
		},
		{
			key: 'POSTPONED',
			icon: faClock,
			color: 'bg-yellow-500 text-white',
			textColor: 'text-yellow-600',
			label: 'Reportée',
			side: true,
		},
		{
			key: 'DRAFT',
			icon: faFileAlt,
			color: 'bg-blue-600 text-white',
			textColor: 'text-blue-600',
			label: 'Brouillon',
		},
		{
			key: 'PLANNED',
			icon: faCalendarDays,
			color: 'bg-purple-500 text-white',
			textColor: 'text-purple-600',
			label: 'Planifiée',
		},
		{
			key: 'BOOKED',
			icon: faBook,
			color: 'bg-purple-500 text-white',
			textColor: 'text-purple-600',
			label: 'Carnet',
		},
		{
			key: 'IN_PROGRESS',
			icon: faCirclePlay,
			color: 'bg-indigo-600 text-white',
			textColor: 'text-indigo-600',
			label: 'En cours',
		},
		{
			key: 'PARTIALLY_INVOICED',
			icon: faSpinner,
			color: 'bg-blue-600 text-white',
			textColor: 'text-blue-600',
			label: 'Partiellement facturée',
		},
		{
			key: 'INVOICED',
			icon: faCircleCheck,
			color: 'bg-green-600 text-white',
			textColor: 'text-green-600',
			label: 'Facturée',
		},
	],

	transitions: {
		CANCELED: [{ to: 'POSTPONED', label: 'Reporter', type: 'main' }],
		POSTPONED: [
			{ to: 'CANCELED', label: 'Annuler', type: 'secondary' },
			{ to: 'DRAFT', label: 'Brouillon', type: 'main' },
		],
		DRAFT: [
			{ to: 'POSTPONED', label: 'Reporter', type: 'secondary' },
			{ to: 'PLANNED', label: 'Planifier', type: 'main' },
		],
		PLANNED: [
			{ to: 'DRAFT', label: 'Brouillon', type: 'secondary' },
			{ to: 'BOOKED', label: 'Carnet', type: 'main' },
		],
		BOOKED: [
			{ to: 'CANCELED', label: 'Annuler', type: 'secondary' },
			{ to: 'POSTPONED', label: 'Reporter', type: 'secondary' },
			{ to: 'PLANNED', label: 'Planifiée', type: 'secondary' },
			{ to: 'IN_PROGRESS', label: 'En cours', type: 'main' },
		],
		IN_PROGRESS: [
			{ to: 'BOOKED', label: 'Carnet', type: 'secondary' },
			{ to: 'PARTIALLY_INVOICED', label: 'Facture partielle', type: 'main' },
		],
		PARTIALLY_INVOICED: [
			{ to: 'IN_PROGRESS', label: 'En cours', type: 'secondary' },
			{ to: 'INVOICED', label: 'Facturer', type: 'main' },
		],
		INVOICED: [
			{ to: 'PARTIALLY_INVOICED', label: 'Annuler', type: 'secondary' },
		],
	},
};
